'use client';

import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { MatchCompletedEnum, MatchStatusApiMap, MatchStatusEnum } from '@/entities/matchup/constants';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';

interface Props {
	matchupUuid: string;
	matchId: string;
	onCancel: () => void;
	onCancelMatchSuccess?: () => void;
	matchStatus: number | null;
}

export const CancelMatchModalContent = ({ onCancel, matchupUuid, matchId, onCancelMatchSuccess, matchStatus }: Props) => {
	const queryClient = useQueryClient();

	const isMatchCompleted = matchStatus === MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE;

	const { mutate: updateMatchMutation, isPending } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success(isMatchCompleted ? 'Match uncancelled successfully.' : 'Match cancelled successfully.');
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupUuid) });
			onCancelMatchSuccess?.();
		}
	});

	const onConfirm = () => {
		updateMatchMutation({
			matchId: matchId,
			updateData: {
				matchStatus: isMatchCompleted
					? MatchStatusApiMap[MatchStatusEnum.SCHEDULED_MATCH_STATUS]
					: MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS],
				matchCompletedType: isMatchCompleted ? MatchCompletedEnum.MATCH_NOT_COMPLETED_TYPE : MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
			}
		});
	};

	return (
		<div className="flex flex-col gap-6">
			<div className="relative w-full">
				<p className="text-black text-2xl font-medium">{isMatchCompleted ? 'Uncancel Match' : 'Cancel Match'}</p>
				<FontAwesomeIcon
					icon={faClose}
					className="cursor-pointer text-xl font-normal absolute right-0 top-1/2 -translate-y-1/2"
					onClick={onCancel}
				/>
			</div>
			<p className="text-gray-900 font-semibold text-sm">
				{isMatchCompleted
					? 'Are you sure you want to uncancel this match?'
					: 'Are you sure you want to cancel this match? Canceled matches are not be recognized for statistics.'}
			</p>
			<div className="flex gap-4 w-full justify-end">
				<Button className={`h-10 w-[7.5rem] text-sm font-semibold leading-5`} onClick={onCancel} variant="outline">
					Close
				</Button>
				<Button
					type="submit"
					onClick={onConfirm}
					disabled={isPending}
					className={`h-10 w-[7.5rem] text-sm font-semibold leading-5 disabled:cursor-not-allowed`}
				>
					Save
				</Button>
			</div>
		</div>
	);
};
